import { useEffect, useRef, useState, useCallback } from 'react'
import { SelfieSegmentation, type InputImage, type Results } from '@mediapipe/selfie_segmentation'
import { apiFetch } from '../lib/apiFetch'

// ── Types ──

export type VBgMode = 'none' | 'blur-light' | 'blur-heavy' | 'image' | 'custom'

export interface VBgPreset {
  id: string
  label: string
  mode: VBgMode
  src?: string // image URL for 'image' mode
}

export const BG_PRESETS: VBgPreset[] = [
  { id: 'none',       label: 'Aucun',        mode: 'none' },
  { id: 'blur-light', label: 'Flou leger',   mode: 'blur-light' },
  { id: 'blur-heavy', label: 'Flou fort',    mode: 'blur-heavy' },
  { id: 'office',     label: 'Bureau',        mode: 'image', src: '/backgrounds/office.svg' },
  { id: 'nature',     label: 'Nature',        mode: 'image', src: '/backgrounds/nature.svg' },
  { id: 'sunset',     label: 'Coucher',       mode: 'image', src: '/backgrounds/sunset.svg' },
  { id: 'abstract',   label: 'Abstrait',      mode: 'image', src: '/backgrounds/abstract.svg' },
  { id: 'minimal',    label: 'Minimal',       mode: 'image', src: '/backgrounds/minimal.svg' },
]

const STORAGE_KEY = 'visio-vbg'
const MAX_FRAME_MS = 15 // degrade if >15ms per frame

// ── Hook ──

export interface VBgDiagnostics {
  stage: 'idle' | 'initializing' | 'ready' | 'error'
  error: string | null
  firstMaskAt: number | null // performance.now() when first mask arrived
}

export function useVirtualBackground(onEvent?: (type: string, data?: Record<string, unknown>) => void) {
  const [activeId, setActiveId] = useState<string>(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'none' } catch { return 'none' }
  })
  const [customSrc, setCustomSrc] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [fps, setFps] = useState(0) // perf indicator
  const [diagnostics, setDiagnostics] = useState<VBgDiagnostics>({ stage: 'idle', error: null, firstMaskAt: null })
  const [uploadError, setUploadError] = useState<string | null>(null)

  const segRef = useRef<SelfieSegmentation | null>(null)
  const maskRef = useRef<ImageBitmap | HTMLCanvasElement | HTMLImageElement | null>(null)
  const bgImageRef = useRef<HTMLImageElement | null>(null)
  const tempCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const busyRef = useRef(false)
  const frameTimesRef = useRef<number[]>([])
  const degradedRef = useRef(false)
  const firstMaskLoggedRef = useRef(false)
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  const preset = BG_PRESETS.find(p => p.id === activeId) || BG_PRESETS[0]
  const mode = activeId === 'custom' ? 'custom' as VBgMode : preset.mode

  // Persist selection
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, activeId) } catch {}
  }, [activeId])

  // Init segmentation engine
  useEffect(() => {
    if (mode === 'none') {
      setReady(false)
      return
    }

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in (globalThis as Record<string, unknown>))
    const initStart = performance.now()
    firstMaskLoggedRef.current = false
    setDiagnostics({ stage: 'initializing', error: null, firstMaskAt: null })
    onEventRef.current?.('vbg.init', { ua, isIOS, mode })

    const seg = new SelfieSegmentation({
      locateFile: (file: string) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${file}`,
    })
    seg.setOptions({ modelSelection: 1, selfieMode: false }) // landscape model (faster)
    seg.onResults((results: Results) => {
      maskRef.current = results.segmentationMask
      busyRef.current = false
      if (!firstMaskLoggedRef.current) {
        firstMaskLoggedRef.current = true
        const dt = Math.round(performance.now() - initStart)
        setDiagnostics(d => ({ ...d, firstMaskAt: performance.now() }))
        onEventRef.current?.('vbg.first-mask', { elapsedMs: dt, mode })
      }
    })
    seg.initialize().then(() => {
      segRef.current = seg
      setReady(true)
      setDiagnostics(d => ({ ...d, stage: 'ready' }))
      onEventRef.current?.('vbg.ready', { elapsedMs: Math.round(performance.now() - initStart), mode, isIOS })
    }).catch(err => {
      const msg = err instanceof Error ? err.message : String(err)
      console.warn('[vbg] segmentation init failed:', err)
      setDiagnostics({ stage: 'error', error: msg, firstMaskAt: null })
      onEventRef.current?.('vbg.error', { stage: 'init', message: msg, ua, isIOS, mode })
    })

    return () => {
      seg.close().catch(() => {})
      segRef.current = null
      setReady(false)
      setDiagnostics({ stage: 'idle', error: null, firstMaskAt: null })
    }
  }, [mode !== 'none']) // eslint-disable-line react-hooks/exhaustive-deps

  // Load background image when preset changes
  useEffect(() => {
    const src = activeId === 'custom' ? customSrc : preset.src
    if (!src) { bgImageRef.current = null; return }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = src
    img.onload = () => { bgImageRef.current = img }
    img.onerror = () => { bgImageRef.current = null }
  }, [activeId, customSrc, preset.src])

  // Set custom image from file upload — server-side scan via Magika before
  // local usage. On 415 we surface a French-localized toast and abort.
  const setCustomImage = useCallback(async (file: File) => {
    setUploadError(null)
    const fd = new FormData()
    fd.append('file', file, file.name)
    fd.append('target', 'background')
    try {
      const res = await apiFetch('/api/visio/upload-background', {
        method: 'POST',
        body: fd,
      })
      if (res.status === 415) {
        const data = await res.json().catch(() => ({} as Record<string, unknown>))
        const reason = (data as { reason?: string }).reason ?? 'type_non_autorise'
        const msg = reason === 'mime_mismatch'
          ? 'Fichier image invalide : type ne correspond pas'
          : 'Fichier refuse : type non autorise comme fond ecran'
        setUploadError(msg)
        return
      }
      if (!res.ok) {
        setUploadError('Fichier refuse : erreur serveur')
        return
      }
    } catch {
      // Reseau KO → degradation gracieuse, usage local autorise.
    }
    const url = URL.createObjectURL(file)
    setCustomSrc(url)
    setActiveId('custom')
  }, [])

  const clearUploadError = useCallback(() => setUploadError(null), [])

  // Clear custom image
  const clearCustom = useCallback(() => {
    if (customSrc) URL.revokeObjectURL(customSrc)
    setCustomSrc(null)
    setActiveId('none')
  }, [customSrc])

  /**
   * Process a frame: segment person, composite with background.
   * Called from the video pipeline draw loop (sync, uses cached mask).
   */
  const processFrame = useCallback((
    video: InputImage & { readyState?: number; HAVE_CURRENT_DATA?: number },
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
  ) => {
    const t0 = performance.now()

    // Request new segmentation (async, double-buffered). Accept any
    // CanvasImageSource: HTMLVideoElement (readyState check), HTMLCanvasElement
    // / ImageBitmap (always ready — no readyState).
    const isReady = video.readyState === undefined || (video.HAVE_CURRENT_DATA !== undefined && video.readyState >= video.HAVE_CURRENT_DATA)
    if (segRef.current && !busyRef.current && isReady) {
      busyRef.current = true
      segRef.current.send({ image: video }).catch(() => { busyRef.current = false })
    }

    const mask = maskRef.current
    if (!mask) {
      // No mask yet — draw raw video
      ctx.drawImage(video, 0, 0, w, h)
      return
    }

    // Ensure temp canvas
    if (!tempCanvasRef.current) {
      tempCanvasRef.current = document.createElement('canvas')
    }
    const tmp = tempCanvasRef.current
    tmp.width = w
    tmp.height = h
    const tCtx = tmp.getContext('2d')!

    // Step 1: Draw background
    if (mode === 'blur-light' || mode === 'blur-heavy') {
      const sigma = mode === 'blur-light' ? 7 : 20
      ctx.save()
      ctx.filter = `blur(${sigma}px)`
      ctx.drawImage(video, 0, 0, w, h)
      ctx.restore()
    } else if ((mode === 'image' || mode === 'custom') && bgImageRef.current) {
      ctx.drawImage(bgImageRef.current, 0, 0, w, h)
    } else {
      ctx.fillStyle = '#1c1c1e'
      ctx.fillRect(0, 0, w, h)
    }

    // Step 2: Draw person using mask as alpha
    // Draw mask to temp canvas, then use it as globalCompositeOperation
    tCtx.clearRect(0, 0, w, h)
    tCtx.drawImage(mask, 0, 0, w, h)

    // Get mask data — person pixels are white (255)
    tCtx.globalCompositeOperation = 'source-in'
    tCtx.drawImage(video, 0, 0, w, h)

    // Step 3: Composite person over background
    ctx.drawImage(tmp, 0, 0, w, h)

    // Perf tracking
    const dt = performance.now() - t0
    const times = frameTimesRef.current
    times.push(dt)
    if (times.length > 30) times.shift()
    if (times.length >= 10) {
      const avg = times.reduce((a, b) => a + b, 0) / times.length
      setFps(Math.round(1000 / Math.max(avg, 1)))
      // Graceful degradation: skip segmentation if too slow
      degradedRef.current = avg > MAX_FRAME_MS
    }
  }, [mode])

  return {
    activeId,
    setActiveId,
    mode,
    preset,
    customSrc,
    setCustomImage,
    clearCustom,
    processFrame,
    ready: ready && mode !== 'none',
    fps,
    degraded: degradedRef.current,
    diagnostics,
    uploadError,
    clearUploadError,
  }
}
